import { TextInput, TextArea, SelectInput, Toggle, LinesInput } from "./fields";
import { SectionsEditor, FeatureRowsEditor } from "./RowEditors";

function Panel({ title, children }) {
  return (
    <section className="bg-surface border border-neutral-200 rounded-xl p-6 sm:p-7">
      <h2 className="font-playfair text-xl text-neutral-900 mb-5">{title}</h2>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>;

const STATUS_OPTIONS = [
  { value: "published", label: "Published — visible on the site" },
  { value: "draft", label: "Draft — hidden from the site and sitemap" },
];

function PublishingPanel({ data, update }) {
  return (
    <Panel title="Publishing">
      <SelectInput label="Status" value={data.status || "published"} onChange={(v) => update("status", v || "published")} options={STATUS_OPTIONS} testid="field-status" />
      <Toggle label="Hide from search engines (noindex) — page stays public but is left out of the sitemap" checked={data.noindex} onChange={(v) => update("noindex", v)} testid="field-noindex" />
    </Panel>
  );
}

function SlugInput({ data, onSlugChange, base }) {
  return (
    <TextInput
      label="URL slug"
      value={data.slug}
      onChange={onSlugChange}
      testid="field-slug"
      hint={base ? `${base}/${data.slug || "…"}` : undefined}
    />
  );
}

export function ArticleForm({ data, update, onSlugChange, categories, products, cfg }) {
  const isReview = data.type === "review";
  return (
    <div className="space-y-6">
      <PublishingPanel data={data} update={update} />
      <Panel title="Headline">
        <TextInput label="Title" value={data.title} onChange={(v) => update("title", v)} testid="field-title" />
        <SlugInput data={data} onSlugChange={onSlugChange} base={cfg.publicPath} />
        <TextArea label="Excerpt" rows={3} value={data.excerpt} onChange={(v) => update("excerpt", v)} testid="field-excerpt" />
        <TextInput label="Cover image URL" value={data.image_url} onChange={(v) => update("image_url", v)} testid="field-image-url" hint="Landscape image, ideally 1400px wide" />
      </Panel>
      <Panel title="Article body">
        <SectionsEditor value={data.sections} onChange={(v) => update("sections", v)} />
      </Panel>
      <Panel title="Details">
        <Grid>
          <SelectInput label="Category" value={data.category} onChange={(v) => update("category", v)} options={categories} testid="field-category" />
          {isReview && (
            <SelectInput label="Reviewed product" value={data.related_product_slug} onChange={(v) => update("related_product_slug", v)} options={products} placeholder="None" testid="field-related-product" />
          )}
          <TextInput label="Author" value={data.author} onChange={(v) => update("author", v)} testid="field-author" />
          <TextInput label="Author title" value={data.author_title} onChange={(v) => update("author_title", v)} testid="field-author-title" />
          <TextInput label="Published" type="date" value={data.publication_date} onChange={(v) => update("publication_date", v)} testid="field-publication-date" />
          <TextInput label="Updated" type="date" value={data.updated_date} onChange={(v) => update("updated_date", v)} testid="field-updated-date" />
          <TextInput label="Reading time (min)" type="number" min="1" value={data.reading_time} onChange={(v) => update("reading_time", v)} testid="field-reading-time" />
        </Grid>
        <Toggle label="Mark as featured" checked={data.featured} onChange={(v) => update("featured", v)} testid="field-featured" />
      </Panel>
      <Panel title="SEO">
        <TextInput label="SEO title" value={data.seo_title} onChange={(v) => update("seo_title", v)} testid="field-seo-title" />
        <TextArea label="SEO description" rows={2} value={data.seo_description} onChange={(v) => update("seo_description", v)} testid="field-seo-description" />
      </Panel>
    </div>
  );
}

export function ComparisonForm({ data, update, onSlugChange, categories, cfg }) {
  const a = data.product_a || "Product A";
  const b = data.product_b || "Product B";
  return (
    <div className="space-y-6">
      <PublishingPanel data={data} update={update} />
      <Panel title="Headline">
        <TextInput label="Title" value={data.title} onChange={(v) => update("title", v)} testid="field-title" />
        <SlugInput data={data} onSlugChange={onSlugChange} base={cfg.publicPath} />
        <Grid>
          <TextInput label="Product A" value={data.product_a} onChange={(v) => update("product_a", v)} testid="field-product-a" />
          <TextInput label="Product B" value={data.product_b} onChange={(v) => update("product_b", v)} testid="field-product-b" />
        </Grid>
        <TextArea label="Excerpt" rows={3} value={data.excerpt} onChange={(v) => update("excerpt", v)} testid="field-excerpt" />
      </Panel>
      <Panel title="Verdict">
        <TextArea label="Quick verdict" rows={3} value={data.quick_verdict} onChange={(v) => update("quick_verdict", v)} testid="field-quick-verdict" />
        <Grid>
          <TextArea label={`Best for — ${a}`} value={data.best_for_a} onChange={(v) => update("best_for_a", v)} testid="field-best-for-a" />
          <TextArea label={`Best for — ${b}`} value={data.best_for_b} onChange={(v) => update("best_for_b", v)} testid="field-best-for-b" />
        </Grid>
      </Panel>
      <Panel title="Feature table">
        <FeatureRowsEditor value={data.feature_rows} onChange={(v) => update("feature_rows", v)} labelA={data.product_a} labelB={data.product_b} />
      </Panel>
      <Panel title="Strengths & limitations">
        <Grid>
          <LinesInput label={`${a} strengths`} value={data.strengths_a} onChange={(v) => update("strengths_a", v)} testid="field-strengths-a" />
          <LinesInput label={`${b} strengths`} value={data.strengths_b} onChange={(v) => update("strengths_b", v)} testid="field-strengths-b" />
          <LinesInput label={`${a} limitations`} value={data.limitations_a} onChange={(v) => update("limitations_a", v)} testid="field-limitations-a" />
          <LinesInput label={`${b} limitations`} value={data.limitations_b} onChange={(v) => update("limitations_b", v)} testid="field-limitations-b" />
        </Grid>
        <LinesInput label="Use cases" value={data.use_cases} onChange={(v) => update("use_cases", v)} testid="field-use-cases" />
      </Panel>
      <Panel title="Pricing & recommendation">
        <TextArea label="Pricing note" rows={2} value={data.pricing_note} onChange={(v) => update("pricing_note", v)} testid="field-pricing-note" />
        <TextArea label="Final recommendation" rows={3} value={data.final_recommendation} onChange={(v) => update("final_recommendation", v)} testid="field-final-recommendation" />
        <Grid>
          <SelectInput label="Category" value={data.category} onChange={(v) => update("category", v)} options={categories} testid="field-category" />
          <TextInput label="Updated" type="date" value={data.updated_date} onChange={(v) => update("updated_date", v)} testid="field-updated-date" />
          <TextInput label="Reading time (min)" type="number" min="1" value={data.reading_time} onChange={(v) => update("reading_time", v)} testid="field-reading-time" />
        </Grid>
      </Panel>
    </div>
  );
}

export function ProductForm({ data, update, onSlugChange, categories }) {
  return (
    <div className="space-y-6">
      <Panel title="Product">
        <TextInput label="Name" value={data.name} onChange={(v) => update("name", v)} testid="field-name" />
        <SlugInput data={data} onSlugChange={onSlugChange} />
        <SelectInput label="Category" value={data.category} onChange={(v) => update("category", v)} options={categories} testid="field-category" />
        <TextInput label="Short description" value={data.description} onChange={(v) => update("description", v)} testid="field-description" />
        <TextArea label="Long description" rows={4} value={data.long_description} onChange={(v) => update("long_description", v)} testid="field-long-description" />
      </Panel>
      <Panel title="Links & appearance">
        <Grid>
          <TextInput label="Website URL" value={data.website_url} onChange={(v) => update("website_url", v)} testid="field-website-url" />
          <TextInput label="Affiliate URL" value={data.affiliate_url} onChange={(v) => update("affiliate_url", v)} testid="field-affiliate-url" hint="Used for 'Visit' buttons when set" />
          <TextInput label="Logo image URL" value={data.logo_url} onChange={(v) => update("logo_url", v)} testid="field-logo-url" hint="Square logo; leave empty to show the initial" />
          <TextInput label="Logo initial" maxLength={2} value={data.logo_initial} onChange={(v) => update("logo_initial", v)} testid="field-logo-initial" />
          <TextInput label="Accent colour" type="color" value={data.accent_color} onChange={(v) => update("accent_color", v)} testid="field-accent-color" className="[&_input]:h-11 [&_input]:p-1" />
        </Grid>
        <Toggle label="Feature on homepage" checked={data.featured} onChange={(v) => update("featured", v)} testid="field-featured" />
      </Panel>
    </div>
  );
}

export function CategoryForm({ data, update, onSlugChange, cfg }) {
  return (
    <Panel title="Category">
      <TextInput label="Name" value={data.name} onChange={(v) => update("name", v)} testid="field-name" />
      <SlugInput data={data} onSlugChange={onSlugChange} base={cfg.publicPath} />
      <TextArea label="Intro" rows={2} value={data.intro} onChange={(v) => update("intro", v)} testid="field-intro" />
      <TextArea label="Description" rows={4} value={data.description} onChange={(v) => update("description", v)} testid="field-description" />
    </Panel>
  );
}
